import { z } from 'zod';
import { IdSchema, MoneySchema } from './common.js';

const QtySchema = z.coerce.number().int().min(1).max(1_000_000);
const NoteSchema = z.string().trim().max(500).default('');

/** Resultado de toda operación de negocio: el documento creado (o el ya existente si se repite la clave). */
export const DocResultSchema = z.object({
  document_id: z.number().int(),
  doc_no: z.string(),
  status: z.string(),
  total: z.number().optional(),
  shipment_status: z.string().optional(),
  adjusted_lines: z.number().int().optional(),
  reversed_document_id: z.number().int().optional(),
});
export type DocResult = z.infer<typeof DocResultSchema>;

// ----- Entradas -----
export const EntryLineSchema = z.object({
  variant_id: IdSchema,
  qty: QtySchema,
  unit_cost: MoneySchema.default(0),
  landed_cost: MoneySchema.optional(),
});
export const CurveEntrySchema = z.object({
  curve_id: IdSchema,
  factor: z.union([z.literal(1), z.literal(0.5)]),
  unit_cost: MoneySchema.default(0),
});
export const EntryBodySchema = z.object({
  location_id: IdSchema,
  lines: z.array(EntryLineSchema).max(2000).default([]),
  curve: CurveEntrySchema.nullish(),
  supplier: z.string().trim().max(120).default(''),
  note: NoteSchema,
});
export type EntryBody = z.input<typeof EntryBodySchema>;

// ----- Despachos y recepción -----
export const ShipmentBodySchema = z.object({
  origin_id: IdSchema,
  destination_id: IdSchema,
  lines: z.array(z.object({ variant_id: IdSchema, qty: QtySchema, unit_price: MoneySchema.optional() })).min(1).max(2000),
  note: NoteSchema,
});
export type ShipmentBody = z.input<typeof ShipmentBodySchema>;

const CountQty = z.coerce.number().int().min(0).max(1_000_000);
export const ReceiveBodySchema = z.object({
  lines: z.array(z.object({ line_id: IdSchema, received: CountQty, missing: CountQty.default(0), damaged: CountQty.default(0), surplus: CountQty.default(0) })).min(1).max(2000),
  note: NoteSchema,
});
export type ReceiveBody = z.input<typeof ReceiveBodySchema>;

export const ShipmentLineSchema = z.object({
  line_id: z.number().int(),
  variant_id: z.number().int(),
  sku: z.string(),
  ref: z.string(),
  product_name: z.string(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  qty: z.number().int(),
  unit_price: z.number(),
  received_qty: z.number().int().nullable(),
  missing_qty: z.number().int().nullable(),
  damaged_qty: z.number().int().nullable(),
  landed_cost: z.number().nullable(),
});
export const ShipmentStatusSchema = z.enum(['DISPATCHED', 'RECEIVED', 'RECEIVED_WITH_DIFFERENCE', 'CANCELLED']);
export const ShipmentSchema = z.object({
  id: z.number().int(),
  doc_no: z.string(),
  status: ShipmentStatusSchema,
  origin_id: z.number().int(),
  origin_code: z.string(),
  origin_name: z.string(),
  destination_id: z.number().int(),
  destination_code: z.string(),
  destination_name: z.string(),
  dispatched_at: z.string(),
  created_by_name: z.string().nullable(),
  note: z.string(),
  total: z.number().nullable(),
  received_at: z.string().nullable(),
  receipt_document_id: z.number().int().nullable(),
  hours_in_transit: z.number(),
  units: z.number().int(),
  lines: z.array(ShipmentLineSchema),
});
export type Shipment = z.infer<typeof ShipmentSchema>;
export const ShipmentListQuerySchema = z.object({
  status: z.enum(['DISPATCHED', 'RECEIVED', 'RECEIVED_WITH_DIFFERENCE', 'CANCELLED', 'OPEN', 'CLOSED']).optional(),
  destination_id: IdSchema.optional(),
  origin_id: IdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export const ReceptionDifferenceSchema = z.object({
  id: z.number().int(),
  status: z.string(),
  created_at: z.string(),
  shipment_document_id: z.number().int(),
  shipment_no: z.string(),
  receipt_no: z.string(),
  destination_id: z.number().int(),
  destination_name: z.string(),
  sku: z.string(),
  ref: z.string(),
  product_name: z.string(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  origin_id: z.number().int(),
  origin_name: z.string(),
  dispatched_qty: z.number().int(),
  received_qty: z.number().int(),
  missing_qty: z.number().int(),
  damaged_qty: z.number().int(),
  surplus_qty: z.number().int(),
  note: z.string(),
  user_name: z.string().nullable(),
  /** Cómo se resolvió el sobrante: aceptado al inventario o devuelto. */
  resolution: z.enum(['ACCEPTED', 'REJECTED']).nullable(),
  resolution_note: z.string(),
  resolved_by: z.number().int().nullable(),
  resolved_by_name: z.string().nullable(),
  resolved_at: z.string().nullable(),
  adjustment_document_id: z.number().int().nullable(),
  adjustment_no: z.string().nullable(),
});

export const DifferenceQuerySchema = z.object({
  status: z.enum(['OPEN', 'RESOLVED', 'REVERSED']).optional(),
  only_surplus: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});

export const ResolveSurplusBodySchema = z.object({
  accept: z.boolean(),
  reason: z.string().trim().min(1).max(300),
});
export const ResolveSurplusResultSchema = z.object({
  difference_id: z.number().int(),
  resolution: z.enum(['ACCEPTED', 'REJECTED']),
  document_id: z.number().int().nullable(),
  surplus_qty: z.number().int(),
});
export type ReceptionDifference = z.infer<typeof ReceptionDifferenceSchema>;

// ----- Ventas y devoluciones -----
export const PriceListSchema = z.enum(['retail', 'wholesale', 'internal', 'promo']);
export type PriceList = z.infer<typeof PriceListSchema>;
export const SaleChannelSchema = z.enum(['store', 'direct']);
export const SaleBodySchema = z.object({
  location_id: IdSchema,
  channel: SaleChannelSchema,
  price_list: PriceListSchema.default('retail'),
  lines: z.array(z.object({ variant_id: IdSchema, qty: QtySchema, unit_price: MoneySchema })).min(1).max(500),
  payments: z.array(z.object({ payment_method_id: IdSchema, amount: MoneySchema })).min(1).max(10),
  customer: z.object({ name: z.string().trim().max(120), document: z.string().trim().max(40).optional(), phone: z.string().trim().max(40).optional() }).nullish(),
  note: NoteSchema,
});
export type SaleBody = z.input<typeof SaleBodySchema>;

export const SaleLineSchema = z.object({
  sale_line_id: z.number().int(),
  variant_id: z.number().int(),
  sku: z.string(),
  ref: z.string(),
  product_name: z.string(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  qty: z.number().int(),
  returned_qty: z.number().int(),
  suggested_price: z.number(),
  unit_price: z.number(),
  negotiated: z.boolean(),
  below_min: z.boolean(),
  unit_cost: z.number().nullable(),
});
export const SaleSchema = z.object({
  id: z.number().int(),
  doc_no: z.string(),
  status: z.enum(['ACTIVE', 'REVERSED']),
  location_id: z.number().int(),
  location_code: z.string(),
  location_name: z.string(),
  sold_at: z.string(),
  seller_name: z.string().nullable(),
  channel: SaleChannelSchema,
  price_list: PriceListSchema,
  total: z.number(),
  credit: z.number(),
  note: z.string(),
  units: z.number().int(),
  returned_units: z.number().int(),
  lines: z.array(SaleLineSchema),
  payments: z.array(z.object({ payment_method_id: z.number().int(), code: z.string(), name: z.string(), kind: z.string(), amount: z.number() })),
  cost_total: z.number().nullable(),
});
export type Sale = z.infer<typeof SaleSchema>;
export const SaleListQuerySchema = z.object({
  location_id: IdSchema.optional(),
  channel: SaleChannelSchema.optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

// Facturas de la Bodega: cada venta (directa a cliente o a un local) con su detalle y a quién se vendió.
export const BodegaSaleLineSchema = z.object({
  ref: z.string(),
  name: z.string(),
  qty: z.number(),
  unit_price: z.number(),
});
export const BodegaSaleSchema = z.object({
  document_id: z.number().int(),
  doc_no: z.string(),
  created_at: z.string(),
  kind: z.enum(['DIRECT', 'INTERNAL']),
  buyer: z.string(),
  total: z.number(),
  lines: z.array(BodegaSaleLineSchema),
});
export type BodegaSale = z.infer<typeof BodegaSaleSchema>;
export const BodegaSalesSchema = z.object({
  from: z.string(),
  to: z.string(),
  today: z.string(),
  sales: z.array(BodegaSaleSchema),
});
export type BodegaSales = z.infer<typeof BodegaSalesSchema>;
export const BodegaSalesQuerySchema = z.object({
  from: z.string().date().optional(),
  to: z.string().date().optional(),
});

export const ReturnBodySchema = z.object({
  lines: z.array(z.object({ sale_line_id: IdSchema, qty: QtySchema })).min(1).max(500),
  reason: z.string().trim().max(300),
  refund_method: z.string().trim().max(60).default(''),
  note: NoteSchema,
});
export type ReturnBody = z.input<typeof ReturnBodySchema>;

// ----- Conteos y ajustes -----
export const CountBodySchema = z.object({
  location_id: IdSchema,
  lines: z.array(z.object({ variant_id: IdSchema, physical_qty: CountQty })).min(1).max(5000),
  note: NoteSchema,
  full: z.boolean().default(false),
});
export type CountBody = z.input<typeof CountBodySchema>;
export const CountCommitBodySchema = z.object({ reason: z.string().trim().max(300), confirm_full: z.boolean().default(false) });
export const CountSchema = z.object({
  id: z.number().int(),
  doc_no: z.string(),
  status: z.string(),
  location_id: z.number().int(),
  location_code: z.string(),
  location_name: z.string(),
  created_at: z.string(),
  created_by_name: z.string().nullable(),
  note: z.string(),
  full_count: z.boolean().nullable(),
  reason: z.string().nullable(),
  confirmed_at: z.string().nullable(),
  adjusted_lines: z.number().int().nullable(),
  lines: z.array(z.object({
    variant_id: z.number().int(), sku: z.string(), ref: z.string(), product_name: z.string(), color: z.string().nullable(), size: z.string().nullable(),
    physical_qty: z.number().int(), system_qty: z.number().int().nullable(), difference: z.number().int().nullable(), unit_cost: z.number().nullable(),
  })),
});
export type Count = z.infer<typeof CountSchema>;

// ----- Reversos -----
export const ReverseBodySchema = z.object({ reason: z.string().trim().max(300).default('') });

// ----- Inventario y Kardex -----
export const StockRowSchema = z.object({
  location_id: z.number().int(),
  location_code: z.string(),
  location_name: z.string(),
  variant_id: z.number().int(),
  sku: z.string(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  product_id: z.number().int(),
  ref: z.string(),
  product_name: z.string(),
  brand: z.string(),
  category: z.string(),
  min_stock: z.number().int(),
  qty: z.number().int(),
  avg_cost: z.number().nullable(),
  avg_landed_cost: z.number().nullable(),
  value_landed: z.number().nullable(),
  /** Precios de venta del producto, para el punto de venta. */
  retail_price: z.number().nullable(),
  wholesale_price: z.number().nullable(),
  internal_price: z.number().nullable(),
  promo_price: z.number().nullable(),
  min_sale_price: z.number().nullable(),
});
export type StockRow = z.infer<typeof StockRowSchema>;
export const StockQuerySchema = z.object({
  location_id: IdSchema.optional(),
  q: z.string().trim().max(80).optional(),
  only_available: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
  /** Variantes puntuales separadas por coma (saldo del sistema en un conteo). */
  variant_ids: z.string().regex(/^\d+(,\d+){0,499}$/).optional(),
  limit: z.coerce.number().int().min(1).max(5000).default(2000),
});

export const StockByProductSchema = z.object({
  location_id: z.number().int(),
  location_code: z.string(),
  location_name: z.string(),
  product_id: z.number().int(),
  ref: z.string(),
  product_name: z.string(),
  brand: z.string(),
  category: z.string(),
  stock_granularity: z.string(),
  min_stock: z.number().int(),
  qty: z.number().int(),
  variants_with_stock: z.number().int(),
  value_landed: z.number().nullable(),
  incoming_transit: z.number().int(),
});
export type StockByProduct = z.infer<typeof StockByProductSchema>;
export const StockByProductQuerySchema = z.object({
  location_id: IdSchema.optional(),
  q: z.string().trim().max(80).optional(),
  /** Paginación en el servidor; el total del filtro viaja en X-Total-Count y sus unidades en X-Total-Units. */
  limit: z.coerce.number().int().min(1).max(500).default(25),
  offset: z.coerce.number().int().min(0).default(0),
});
/** Totales de existencias de una ubicación (o de todas las que el usuario puede ver), sin traer las filas. */
export const StockSummarySchema = z.object({ refs: z.number().int(), units: z.number().int(), value_landed: z.number().nullable() });
export type StockSummary = z.infer<typeof StockSummarySchema>;

export const MovementSchema = z.object({
  id: z.number().int(),
  occurred_at: z.string(),
  movement_type: z.string(),
  document_id: z.number().int().nullable(),
  doc_no: z.string().nullable(),
  doc_type: z.string().nullable(),
  document_status: z.string().nullable(),
  variant_id: z.number().int(),
  sku: z.string(),
  ref: z.string(),
  product_name: z.string(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  location_id: z.number().int().nullable(),
  origin_id: z.number().int().nullable(),
  destination_id: z.number().int().nullable(),
  qty: z.number().int(),
  unit_price: z.number().nullable(),
  unit_cost: z.number().nullable(),
  unit_landed_cost: z.number().nullable(),
  user_name: z.string().nullable(),
  related_movement_id: z.number().int().nullable(),
  note: z.string().nullable(),
});
export type Movement = z.infer<typeof MovementSchema>;
export const MovementQuerySchema = z.object({
  location_id: IdSchema.optional(),
  movement_type: z.string().trim().max(40).optional(),
  q: z.string().trim().max(80).optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  limit: z.coerce.number().int().min(1).max(2000).default(300),
  /** Paginación: filas a saltar. El total del filtro viaja en la cabecera X-Total-Count. */
  offset: z.coerce.number().int().min(0).default(0),
});

export const TransitRowSchema = z.object({
  document_id: z.number().int(),
  doc_no: z.string(),
  origin_id: z.number().int(),
  destination_id: z.number().int(),
  dispatched_at: z.string(),
  hours_in_transit: z.number(),
  line_id: z.number().int(),
  variant_id: z.number().int(),
  sku: z.string(),
  ref: z.string(),
  product_name: z.string(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  qty: z.number().int(),
  unit_price: z.number(),
  landed_cost: z.number().nullable(),
});

/** Estado de integridad: saldo = suma del Kardex (debe estar vacío). */
export const KardexCheckSchema = z.object({ ok: z.boolean(), differences: z.number().int() });

// ----- Importación de inventario desde Excel (M3) -----
export const InventoryImportModeSchema = z.enum(['audit', 'sum', 'replace']);
export type InventoryImportMode = z.infer<typeof InventoryImportModeSchema>;
export const InventoryImportRowSchema = z.object({
  row: z.number().int(),
  location_code: z.string().trim().max(12).optional(),
  ref: z.string().trim().max(60),
  color: z.string().trim().max(40).optional(),
  size: z.string().trim().max(20).optional(),
  /** Se valida en la base de datos para que la vista previa muestre el error en su fila. */
  qty: z.union([z.number(), z.string().max(20)]).nullable(),
  unit_cost: MoneySchema.nullable().optional(),
});
export type InventoryImportRow = z.infer<typeof InventoryImportRowSchema>;
export const InventoryImportBodySchema = z.object({
  location_id: IdSchema.nullish(),
  mode: InventoryImportModeSchema,
  rows: z.array(InventoryImportRowSchema).min(1).max(20000),
  source: z.string().trim().max(120).default('excel'),
  reason: z.string().trim().max(300).default(''),
  full: z.boolean().default(false),
});
export type InventoryImportBody = z.input<typeof InventoryImportBodySchema>;
export const InventoryImportResultSchema = z.object({
  mode: InventoryImportModeSchema,
  phase: z.enum(['preview', 'commit']),
  errors: z.number().int(),
  skipped: z.number().int(),
  rows: z.array(z.object({
    row: z.number().int().nullable(),
    location_code: z.string().nullable(),
    ref: z.string(),
    color: z.string().nullable(),
    size: z.string().nullable(),
    qty: z.number().nullable(),
    system_qty: z.number().int().nullable(),
    status: z.enum(['ok', 'skip', 'error']),
    reason: z.string().nullable(),
  })),
  locations: z.array(z.object({ location_id: z.number().int(), location_code: z.string(), lines: z.number().int(), units: z.number().int(), system_units: z.number().int() })),
  documents: z.array(z.object({ document_id: z.number().int(), doc_no: z.string(), status: z.string(), location_code: z.string() })),
});
export type InventoryImportResult = z.infer<typeof InventoryImportResultSchema>;

// ----- Despacho masivo por Excel -----
export const DispatchImportBodySchema = z.object({
  origin_id: IdSchema,
  rows: z.array(z.object({
    row: z.number().int(),
    ref: z.string().trim().max(60),
    color: z.string().trim().max(40).optional(),
    size: z.string().trim().max(20).optional(),
    qty: z.union([z.number(), z.string().max(20)]).nullable(),
    unit_price: MoneySchema.nullable().optional(),
  })).min(1).max(5000),
});
export type DispatchImportRow = z.infer<typeof DispatchImportBodySchema>['rows'][number];
export const DispatchImportRowResultSchema = z.object({
  row: z.number().int().nullable(),
  ref: z.string(),
  product_name: z.string().nullable(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  variant_id: z.number().int().nullable(),
  qty: z.number().nullable(),
  unit_price: z.number(),
  available: z.number().int().nullable(),
  status: z.enum(['ok', 'error']),
  reason: z.string().nullable(),
});
export type DispatchImportRowResult = z.infer<typeof DispatchImportRowResultSchema>;

// ----- Dashboard gerencial -----
const SalesPeriodSchema = z.object({ total: z.number(), count: z.number().int(), units: z.number(), collected: z.number(), returns: z.number() });
export const SalesPeriodTotalsSchema = z.object({
  total: z.number(), count: z.number().int(),
  /** Unidades netas de devoluciones y unidades vendidas en bruto. */
  units: z.number().int(), units_sold: z.number().int(),
  returns: z.number(), returned_units: z.number().int(),
  collected: z.number(), cost: z.number(), margin: z.number(), margin_pct: z.number(),
  store: z.number(), direct: z.number(),
});

export const DashboardSchema = z.object({
  /** Período consultado (fechas de Bogotá) y cómo se agrupa la gráfica. */
  from: z.string(), to: z.string(), days: z.number().int(), bucket: z.enum(['day', 'week', 'month']), today: z.string(),
  previous: z.object({ from: z.string(), to: z.string(), total: z.number() }),
  period: z.object({
    sales: SalesPeriodTotalsSchema,
    by_method: z.array(z.object({ payment_method_id: z.number().int(), code: z.string(), name: z.string(), kind: z.string(), collected: z.boolean(), amount: z.number() })),
    daily: z.array(z.object({ day: z.string(), total: z.number(), count: z.number().int() })),
    top_location: z.object({ code: z.string(), name: z.string(), total: z.number() }).nullable(),
    top_product: z.object({ ref: z.string(), name: z.string(), units: z.number().int(), total: z.number() }).nullable(),
    top_margin: z.array(z.object({ ref: z.string(), name: z.string(), units: z.number().int(), sales: z.number(), margin: z.number() })),
  }),
  /** Saldos actuales: no dependen del período consultado. */
  current: z.object({
    inventory: z.array(z.object({ location_id: z.number().int(), code: z.string(), name: z.string(), type: z.enum(['WAREHOUSE', 'STORE']), min_total: z.number().int(), units: z.number().int(), value: z.number(), value_internal: z.number().default(0) })),
    transit: z.object({ units: z.number(), value: z.number() }),
    pending_shipments: z.object({ count: z.number().int(), oldest_hours: z.number(), items: z.array(z.object({ doc_no: z.string(), destination: z.string(), origin: z.string(), units: z.number(), hours: z.number() })) }),
    open_differences: z.object({ count: z.number().int(), missing: z.number(), damaged: z.number(), surplus: z.number() }),
    low_stock: z.object({ count: z.number().int(), items: z.array(z.object({ location_code: z.string(), ref: z.string(), product_name: z.string(), qty: z.number().int(), min_stock: z.number().int() })) }),
    below_cost: z.number().int(),
    kardex_differences: z.number().int(),
  }),
});

/** Período a consultar: sin fechas es hoy; `all` abarca toda la historia. */
export const PeriodQuerySchema = z.object({
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  all: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
});

export type Dashboard = z.infer<typeof DashboardSchema>;
